import * as THREE from "three";
import { Camera } from "three";
import Action from '../classes/Action';
import DefaultSceneManager from '../classes/DefaultSceneManager';
import { CustomEventTypes, SaveModificationTypes, Space, TimelineEffectorTypes } from '../globals';
import Engine from '../classes/Engine';
import Scenario from "../classes/scenarioElements/Scenario";
import ProjectSaver from '../classes/ProjectSaver';
import store from '../state/store';
import MathUtils from '../classes/utilities/MathUtils';

export default class AlignToViewAction extends Action
{
	constructor(objects: THREE.Object3D[], camera: Camera, treatObjectsAsGroup = true)
	{
		const actionName = objects.length > 1 ? "Aligned objects to view" : objects.length > 0 ? "Aligned " + objects[0].name + " to view" : "";
		const isDefaultState = Scenario.currentStep()?.isDefault();

		const oldPositions = Array(objects.length);
		const oldRotations = Array(objects.length);
		const newPositions = Array(objects.length);
		const newRotations = Array(objects.length);
		const previousParents = Array(objects.length);
		const gizmoObject = Engine.getInstance().getGizmoManager().control.dummyObject;

		for (let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			oldPositions[i] = new THREE.Vector3().copy(object.position);
			oldRotations[i] = new THREE.Euler().copy(object.rotation);
			previousParents[i] = object.parent;
			gizmoObject.attach(object);
			if(!treatObjectsAsGroup)
			{
				object.position.set(0, 0, 0);
				object.rotation.set(0, 0, 0);
			}
		}
		gizmoObject.position.copy(camera.position);
		gizmoObject.quaternion.copy(camera.quaternion);

		for (let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			previousParents[i].attach(object);
			newPositions[i] = new THREE.Vector3().copy(object.position);
			newRotations[i] = new THREE.Euler().copy(object.rotation);
			object.position.copy(oldPositions[i]);
			object.rotation.setFromVector3(oldRotations[i]);
		}

		super([objects, newPositions, newRotations, isDefaultState],
			[objects, oldPositions, oldRotations, isDefaultState],
			actionName,	[objects, newPositions, oldPositions, newRotations, oldRotations]);
	}

	do(objects: THREE.Object3D[], positions: THREE.Vector3[], rotations: THREE.Euler[], isDefaultState: boolean)
	{
		if (isDefaultState)
		{
			DefaultSceneManager.getInstance().syncNativeObjectVectorProperty(objects, 'position', positions);
			DefaultSceneManager.getInstance().syncNativeObjectVectorProperty(objects, 'rotation', rotations);
		}

		for (let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			object.position.copy(positions[i]);
			object.rotation.copy(rotations[i]);
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Position);
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Rotation);

		}

		if(store.getState().timelineModule.editorData.isRecording)
		{
			const translationEventDetail = { animationType: TimelineEffectorTypes.TransformTranslate, space: Space.World };
			const rotationEventDetail = { animationType: TimelineEffectorTypes.TransformRotateSelf };
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onTransformRecordEvent, { detail: translationEventDetail }));
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onTransformRecordEvent, { detail: rotationEventDetail }));
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectPositionChanged));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectRotationChanged));
	}

	verify(objects: THREE.Object3D[], newPositions: THREE.Vector3[], oldPositions: THREE.Vector3[], newRotations: THREE.Euler[], oldRotations: THREE.Euler[])
	{
		if (objects.length === 0) return false;

		let difference = false;
		for (let i = 0; i < objects.length; i++)
		{
			if (!MathUtils.areVectorsEqual(newPositions[i], oldPositions[i]) || !MathUtils.areVectorsEqual(newRotations[i], oldRotations[i]))
			{
				difference = true;
				break;
			}
		}

		if (difference && store.getState().timelineModule.editorData.isRecording) this.flagAsTemporary();
		return difference;
	}
}
